import store from 'Store/index';
import { SET_SELF } from 'Store/reducers/AppReducer';
import logError from 'Helpers/logError';
import UserParamResource from 'App/babel/resources/UserParam';

type SeenMarker = 'lastSeenReleaseNoteId' | 'lastSeenProductNewsNoticeId';

/**
 * The markers' ids in the master param registry (`base/src/core/params/userParams.js`).
 * Hardcoded because the registry lives in base, which the UI cannot import — the same
 * reason NavbarBrandDropdown names its param by number.
 */
const MARKER_PARAMS: Record<SeenMarker, number> = { lastSeenReleaseNoteId: 239, lastSeenProductNewsNoticeId: 241 };

/** Moves a marker in the store, which is what the navbar reads. Never backwards: it only ever grows. */
const adoptMarker = (marker: SeenMarker, seenId: number) => {
	const { self } = store.getState().App;
	if (!self || seenId <= (self.userParams[marker] ?? 0)) {
		return;
	}
	// The raw action rather than `setSelf`: AppActions imports appHelper, which imports this, and
	// `setSelf` would also redo the timezone and locale setup for a read marker.
	store.dispatch({ type: SET_SELF, self: { ...self, userParams: { ...self.userParams, [marker]: seenId } } });
};

const markSeen = (marker: SeenMarker, seenId: number, what: string) => {
	adoptMarker(marker, seenId);
	// Neither outcome is worth telling the reader about — this is a read marker they did
	// not ask for. A failed write just means it counts as unseen next load.
	UserParamResource.save(MARKER_PARAMS[marker], seenId, {
		skipNotification: true,
		skipErrorNotification: true
	}).catch((e: unknown) => logError(e, `Product news: could not mark ${what} as seen`));
};

export const adoptLastSeenReleaseNoteId = (seenId: number) => adoptMarker('lastSeenReleaseNoteId', seenId);

/** The reader has been shown every release up to `seenId`. */
export const markProductNewsSeen = (seenId: number) => markSeen('lastSeenReleaseNoteId', seenId, 'the release notes');

/** The reader has opened the panel the Master notice `noticeId` sits at the top of. */
export const markProductNewsNoticeSeen = (noticeId: number) =>
	markSeen('lastSeenProductNewsNoticeId', noticeId, 'the notice');

/**
 * Read straight off the param rather than the /alliwant body that carries it: that body
 * is cached in redis for 30 seconds and nothing in the user-param write path invalidates
 * it, so it can still report the value from before this user's own last write.
 */
export const readLastSeenReleaseNoteId = (): Promise<number> =>
	UserParamResource.get(MARKER_PARAMS.lastSeenReleaseNoteId, {
		skipErrorNotification: true,
		// Nothing saved yet answers 404, which is "seen nothing" rather than a failure.
		// Anything else is one, and the caller decides what to do with it.
		expectedStatuses: [404]
	})
		.then((res: { data?: { value?: unknown } | null }) => Number(res?.data?.value) || 0)
		.catch((e: { response?: { status?: number } }) => {
			if (e?.response?.status === 404) {
				return 0;
			}
			throw e;
		});
